import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Input from "../../../designSystem/b-molecules/formElements/input/Input";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import { onValue, ref, update, remove, push } from "firebase/database";
import { db } from "../../../configs/Firebase";
import Fuse from "fuse.js";
import style from "./ManageBrands.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";
import { generateThemeId } from "../utils/categorizeFeedback";

const ManageThemes = ({ show, onClose }) => {
  const org = useSelector((state) => state.orgStatus.value);
  const [themes, setThemes] = useState([]);
  const [subThemes, setSubThemes] = useState({}); // { themeId: [subThemes] }
  const [expandedThemes, setExpandedThemes] = useState({}); // { themeId: true/false }
  const [creatingNew, setCreatingNew] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [newThemeName, setNewThemeName] = useState("");
  const [creatingSubTheme, setCreatingSubTheme] = useState(null); // themeId
  const [editingSubTheme, setEditingSubTheme] = useState(null); // { themeId, subTheme }
  const [newSubThemeName, setNewSubThemeName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (org.payload && org.payload !== undefined) {
      const themesRef = ref(db, `userData/${org.payload}/customers/themes`);
      const themesUnsubscribe = onValue(themesRef, (snapshot) => {
        const data = snapshot.val();
        const temp = [];
        if (data) {
          for (let i in data) {
            temp.push({
              id: i,
              ...data[i],
            });
          }
        }
        setThemes(temp);
      });

      return () => {
        themesUnsubscribe();
      };
    }
  }, [org]);

  // Load sub-themes when themes change
  useEffect(() => {
    if (org.payload && org.payload !== undefined && themes.length > 0) {
      const subThemesUnsubscribes = [];
      
      themes.forEach(theme => {
        const subThemesRef = ref(db, `userData/${org.payload}/customers/themes/${theme.id}/subThemes`);
        const subThemesUnsubscribe = onValue(subThemesRef, (subSnapshot) => {
          const subData = subSnapshot.val();
          const subList = [];
          if (subData) {
            for (let j in subData) {
              subList.push({
                id: j,
                ...subData[j],
              });
            }
          }
          setSubThemes(prev => ({
            ...prev,
            [theme.id]: subList
          }));
        });
        subThemesUnsubscribes.push(subThemesUnsubscribe);
      });

      return () => {
        subThemesUnsubscribes.forEach(unsub => unsub());
      };
    }
  }, [org, themes]);

  const fuse = new Fuse(themes, {
    keys: [{ name: "name" }],
    includeMatches: true,
  });
  const [themeResults, setThemeResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  function searchHandler(e) {
    setError("");
    const value = e.target.value;
    setSearchTerm(value);
    if (value) {
      setThemeResults(fuse.search(value));
    } else {
      setThemeResults([]);
    }
  }

  function createNew() {
    if (!newThemeName.trim()) {
      setError("Theme name cannot be empty");
      return;
    }

    if (
      themes.some(
        (t) => t.name.toLowerCase() === newThemeName.trim().toLowerCase()
      )
    ) {
      setError("This theme already exists");
      return;
    }

    if (org.payload) {
      const themeId = generateThemeId(newThemeName.trim());
      const themeRef = ref(
        db,
        `userData/${org.payload}/customers/themes/${themeId}`
      );
      update(themeRef, {
        name: newThemeName.trim(),
        createdAt: { ".sv": "timestamp" },
      });
      setNewThemeName("");
      setCreatingNew(false);
      setError("");
    }
  }

  function startEdit(theme) {
    setEditingTheme(theme);
    setNewThemeName(theme.name);
    setCreatingNew(false);
    setError("");
  }

  function saveEdit() {
    if (!newThemeName.trim()) {
      setError("Theme name cannot be empty");
      return;
    }

    if (
      themes.some(
        (t) =>
          t.id !== editingTheme.id &&
          t.name.toLowerCase() === newThemeName.trim().toLowerCase()
      )
    ) {
      setError("Another theme with this name already exists");
      return;
    }

    if (org.payload && editingTheme) {
      const themeRef = ref(
        db,
        `userData/${org.payload}/customers/themes/${editingTheme.id}`
      );
      update(themeRef, {
        name: newThemeName.trim(),
      });
      setEditingTheme(null);
      setNewThemeName("");
      setError("");
    }
  }

  function deleteTheme(themeId) {
    if (org.payload) {
      // Check if theme has sub-themes
      const themeSubThemes = subThemes[themeId] || [];
      if (themeSubThemes.length > 0) {
        const confirmMsg = `"${themes.find(t => t.id === themeId)?.name}" has ${themeSubThemes.length} sub-theme(s). Deleting the theme will also delete all sub-themes. Continue?`;
        if (!window.confirm(confirmMsg)) {
          return;
        }
        // Delete all sub-themes first
        themeSubThemes.forEach(subTheme => {
          const subThemeRef = ref(
            db,
            `userData/${org.payload}/customers/themes/${themeId}/subThemes/${subTheme.id}`
          );
          remove(subThemeRef);
        });
      }
      const themeRef = ref(
        db,
        `userData/${org.payload}/customers/themes/${themeId}`
      );
      remove(themeRef);
    }
  }

  function toggleExpand(themeId) {
    setExpandedThemes(prev => ({
      ...prev,
      [themeId]: !prev[themeId]
    }));
  }

  function startCreateSubTheme(themeId) {
    setCreatingSubTheme(themeId);
    setNewSubThemeName("");
    setEditingSubTheme(null);
    setError("");
  }

  function startEditSubTheme(themeId, subTheme) {
    setEditingSubTheme({ themeId, subTheme });
    setNewSubThemeName(subTheme.name);
    setCreatingSubTheme(null);
    setError("");
  }

  function createSubTheme(themeId) {
    if (!newSubThemeName.trim()) {
      setError("Sub-theme name cannot be empty");
      return;
    }

    const themeSubThemes = subThemes[themeId] || [];
    if (themeSubThemes.some(st => st.name.toLowerCase() === newSubThemeName.trim().toLowerCase())) {
      setError("This sub-theme already exists for this theme");
      return;
    }

    if (org.payload) {
      const subThemeId = generateThemeId(newSubThemeName.trim());
      const subThemeRef = ref(
        db,
        `userData/${org.payload}/customers/themes/${themeId}/subThemes/${subThemeId}`
      );
      update(subThemeRef, {
        name: newSubThemeName.trim(),
        createdAt: { ".sv": "timestamp" },
      });
      setNewSubThemeName("");
      setCreatingSubTheme(null);
      setError("");
    }
  }

  function saveSubTheme() {
    if (!newSubThemeName.trim()) {
      setError("Sub-theme name cannot be empty");
      return;
    }

    const { themeId, subTheme } = editingSubTheme;
    const themeSubThemes = subThemes[themeId] || [];
    if (themeSubThemes.some(st => st.id !== subTheme.id && st.name.toLowerCase() === newSubThemeName.trim().toLowerCase())) {
      setError("Another sub-theme with this name already exists");
      return;
    }

    if (org.payload) {
      const subThemeRef = ref(
        db,
        `userData/${org.payload}/customers/themes/${themeId}/subThemes/${subTheme.id}`
      );
      update(subThemeRef, {
        name: newSubThemeName.trim(),
      });
      setEditingSubTheme(null);
      setNewSubThemeName("");
      setError("");
    }
  }

  function deleteSubTheme(themeId, subThemeId) {
    if (org.payload) {
      const subThemeRef = ref(
        db,
        `userData/${org.payload}/customers/themes/${themeId}/subThemes/${subThemeId}`
      );
      remove(subThemeRef);
    }
  }

  function cancelSubTheme() {
    setCreatingSubTheme(null);
    setEditingSubTheme(null);
    setNewSubThemeName("");
    setError("");
  }

  function cancel() {
    setCreatingNew(false);
    setEditingTheme(null);
    setNewThemeName("");
    setError("");
    cancelSubTheme();
  }

  const displayThemes = searchTerm ? themeResults.map((r) => r.item) : themes;

  return (
    <Dialog show={show} onClose={onClose} title="Manage Themes" variants={["large"]}>
      <div className={style.input}>
        <Input
          label="Search Themes"
          placeholder=""
          onChange={searchHandler}
          value={searchTerm}
          error={error}
        />
      </div>

      {creatingNew || editingTheme ? (
        <div className={style.editSection}>
          <Input
            label="Theme Name"
            placeholder="Enter theme name"
            onChange={(e) => setNewThemeName(e.target.value)}
            value={newThemeName}
            error={error}
          />
          <div className={style.actions}>
            <Button variants={["text"]} onClick={cancel}>
              Cancel
            </Button>
            <Button
              variants={["primary"]}
              onClick={editingTheme ? saveEdit : createNew}
            >
              {editingTheme ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ul className={style.brandList}>
            {displayThemes.length > 0 ? (
              displayThemes.map((theme) => {
                const themeSubThemes = subThemes[theme.id] || [];
                const isExpanded = expandedThemes[theme.id];
                const isCreatingSub = creatingSubTheme === theme.id;
                const isEditingSub = editingSubTheme?.themeId === theme.id;

                return (
                  <li key={theme.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--padding--small)', flex: 1 }}>
                        <Button
                          variants={["text", "small"]}
                          onClick={() => toggleExpand(theme.id)}
                          style={{ padding: 'var(--padding--xsmall)' }}
                        >
                          <Icon>{isExpanded ? 'expand_less' : 'expand_more'}</Icon>
                        </Button>
                        <span className={style.brandName}>{theme.name}</span>
                        {themeSubThemes.length > 0 && (
                          <span style={{ fontSize: '12px', color: 'var(--on--400)' }}>
                            ({themeSubThemes.length} sub-theme{themeSubThemes.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      <div className={style.brandActions}>
                        <Button
                          variants={["text", "small"]}
                          onClick={() => startEdit(theme)}
                        >
                          <Icon>edit</Icon>
                        </Button>
                        <Button
                          variants={["text", "small"]}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Are you sure you want to delete "${theme.name}"? Existing records will keep this theme id/name.`
                              )
                            ) {
                              deleteTheme(theme.id);
                            }
                          }}
                        >
                          <Icon>delete</Icon>
                        </Button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div style={{ marginLeft: 'var(--padding--large)', marginTop: 'var(--padding--small)' }}>
                        {isCreatingSub || isEditingSub ? (
                          <div style={{ padding: 'var(--padding--small)', backgroundColor: 'var(--on--800)', borderRadius: 'var(--radius--small)', marginBottom: 'var(--padding--small)' }}>
                            <Input
                              label="Sub-theme Name"
                              placeholder="Enter sub-theme name"
                              onChange={(e) => setNewSubThemeName(e.target.value)}
                              value={newSubThemeName}
                              error={error}
                            />
                            <div style={{ display: 'flex', gap: 'var(--padding--small)', marginTop: 'var(--padding--small)' }}>
                              <Button variants={["text", "small"]} onClick={cancelSubTheme}>
                                Cancel
                              </Button>
                              <Button
                                variants={["primary", "small"]}
                                onClick={isEditingSub ? saveSubTheme : () => createSubTheme(theme.id)}
                              >
                                {isEditingSub ? "Save" : "Create"}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variants={["text", "small"]}
                            onClick={() => startCreateSubTheme(theme.id)}
                            style={{ marginBottom: 'var(--padding--small)' }}
                          >
                            <Icon>add</Icon> Add Sub-theme
                          </Button>
                        )}
                        {themeSubThemes.length > 0 ? (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {themeSubThemes.map((subTheme) => (
                              <li key={subTheme.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--padding--small)', backgroundColor: 'var(--on--800)', borderRadius: 'var(--radius--small)', marginBottom: 'var(--padding--xsmall)' }}>
                                <span style={{ color: 'var(--on--200)', fontSize: '13px' }}>{subTheme.name}</span>
                                <div style={{ display: 'flex', gap: 'var(--padding--xsmall)' }}>
                                  <Button
                                    variants={["text", "small"]}
                                    onClick={() => startEditSubTheme(theme.id, subTheme)}
                                  >
                                    <Icon>edit</Icon>
                                  </Button>
                                  <Button
                                    variants={["text", "small"]}
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to delete "${subTheme.name}"?`)) {
                                        deleteSubTheme(theme.id, subTheme.id);
                                      }
                                    }}
                                  >
                                    <Icon>delete</Icon>
                                  </Button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          !isCreatingSub && (
                            <div style={{ fontSize: '12px', color: 'var(--on--400)', padding: 'var(--padding--small)' }}>
                              No sub-themes yet
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </li>
                );
              })
            ) : (
              <li className={style.noResults}>
                {searchTerm ? "No themes found" : "No themes created yet"}
              </li>
            )}
          </ul>
          <div className={style.actions}>
            <Button variants={["text"]} onClick={() => setCreatingNew(true)}>
              Create New Theme
            </Button>
            <Button variants={["text"]} onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default ManageThemes;